import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Trash2, Phone, Mail, Calendar as Cal, Car, Clock, Search, CheckCircle2, X, Plus, DollarSign, CreditCard, Banknote, Download, Image as ImageIcon, Upload, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { compressImage } from "@/lib/imageCompress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rpd_admin_token";

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Login = ({ onAuth }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.email);
      toast.success("Welcome back.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6 noise-overlay">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md bg-[#121212] border border-[#27272A] p-10"
      >
        <div className="flex items-center gap-3 mb-2">
          <Lock className="h-5 w-5 text-[#DC2626]" />
          <span className="font-mono-accent text-xs text-zinc-400">ADMIN ACCESS</span>
        </div>
        <h1 className="font-display font-bold uppercase text-white text-4xl tracking-tighter mb-8">
          Sign in to <br />
          <span className="chrome-text">Revive</span>
        </h1>
        <div className="space-y-6">
          <div>
            <label className="font-mono-accent text-[10px] text-zinc-500 mb-2 block">Email</label>
            <input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border-b border-[#27272A] focus:border-[#DC2626] text-white py-3 outline-none"
              placeholder="admin@reviveprodetailing.com.au"
            />
          </div>
          <div>
            <label className="font-mono-accent text-[10px] text-zinc-500 mb-2 block">Password</label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border-b border-[#27272A] focus:border-[#DC2626] text-white py-3 outline-none"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full bg-[#DC2626] hover:bg-[#EF4444] disabled:opacity-60 text-white font-display font-bold uppercase tracking-wider text-sm py-4 transition-colors"
          >
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </div>
        <a href="/" className="block mt-6 text-center text-zinc-500 hover:text-zinc-300 text-xs font-mono-accent">
          ← BACK TO SITE
        </a>
      </form>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    confirmed: "bg-green-500/10 text-green-400 border-green-500/30",
    completed: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  };
  const cls = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center text-[10px] font-mono-accent border px-2 py-1 ${cls}`}>
      {status?.toUpperCase()}
    </span>
  );
};

const PaymentBadge = ({ method, price }) => {
  if (!method || method === "unpaid")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono-accent border border-red-500/30 bg-red-500/10 text-red-400 px-2 py-1">
        UNPAID{price ? ` · $${price}` : ""}
      </span>
    );
  const Icon = method === "cash" ? Banknote : CreditCard;
  const cls =
    method === "cash"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
      : "border-sky-500/30 bg-sky-500/10 text-sky-400";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono-accent border px-2 py-1 ${cls}`}>
      <Icon className="h-3 w-3" /> {method.toUpperCase()} ${price || 0}
    </span>
  );
};

const SourceBadge = ({ source }) =>
  source === "walk-in" ? (
    <span className="inline-flex items-center text-[10px] font-mono-accent border border-purple-500/30 bg-purple-500/10 text-purple-300 px-2 py-1">
      WALK-IN
    </span>
  ) : null;

const fmtDate = (v) => {
  try {
    return format(new Date(v), "MMM d, yyyy · h:mm a");
  } catch {
    return v;
  }
};

const Dashboard = ({ adminEmail, onLogout }) => {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, g, cust] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeader() }),
        axios.get(`${API}/admin/contacts`, { headers: authHeader() }),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/admin/customers`, { headers: authHeader() }),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setGallery(g.data);
      setCustomers(cust.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: authHeader() });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const markPaid = async (id, payment_method, price) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${id}`,
        { payment_method, price: Number(price) || 0 },
        { headers: authHeader() }
      );
      setBookings((bs) =>
        bs.map((b) => (b.id === id ? { ...b, payment_method, price: Number(price) || 0 } : b))
      );
      toast.success(`Paid by ${payment_method}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const promptPaid = (booking, method) => {
    const current = booking.price ? String(booking.price) : "";
    const v = window.prompt(`Amount paid by ${method} (AUD)?`, current);
    if (v === null) return;
    markPaid(booking.id, method, v);
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeader() });
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const removeContact = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: authHeader() });
      setContacts((cs) => cs.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const patchContact = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}`, patch, { headers: authHeader() });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    } catch {
      toast.error("Update failed");
    }
  };

  const filterText = q.toLowerCase().trim();
  const fb = bookings.filter(
    (b) =>
      !filterText ||
      [b.name, b.email, b.phone, b.vehicle, b.service].some((v) => v?.toLowerCase().includes(filterText))
  );
  const fc = contacts.filter(
    (c) => !filterText || [c.name, c.email, c.phone, c.message].some((v) => v?.toLowerCase().includes(filterText))
  );

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  // Today's take — completed bookings only
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const todayCompleted = bookings.filter((b) => {
    if (b.status !== "completed") return false;
    try {
      return new Date(b.created_at) >= startOfToday;
    } catch {
      return false;
    }
  });
  const todayCash = todayCompleted.filter((b) => b.payment_method === "cash").reduce((s, b) => s + (b.price || 0), 0);
  const todayCard = todayCompleted.filter((b) => b.payment_method === "card").reduce((s, b) => s + (b.price || 0), 0);
  const todayUnpaid = todayCompleted.filter((b) => b.payment_method === "unpaid").length;

  // Revenue this month
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonth = bookings.filter((b) => {
    try {
      return new Date(b.created_at) >= monthStart;
    } catch {
      return false;
    }
  });
  const cashIn = thisMonth.filter((b) => b.payment_method === "cash").reduce((s, b) => s + (b.price || 0), 0);
  const cardIn = thisMonth.filter((b) => b.payment_method === "card").reduce((s, b) => s + (b.price || 0), 0);
  const totalIn = cashIn + cardIn;

  // Enquiry pipeline (this month)
  const monthEnq = contacts.filter((c) => {
    try { return new Date(c.created_at) >= monthStart; } catch { return false; }
  });
  const quotedTotal = monthEnq
    .filter((c) => ["quoted", "won"].includes(c.enquiry_status || "new"))
    .reduce((s, c) => s + (c.quoted_price || 0), 0);
  const wonTotal = monthEnq
    .filter((c) => c.enquiry_status === "won")
    .reduce((s, c) => s + (c.quoted_price || 0), 0);
  const openQuotes = contacts.filter((c) => c.enquiry_status === "quoted").length;

  const [creating, setCreating] = useState(false);
  const addBooking = (b) => setBookings((bs) => [b, ...bs]);

  // Build 6-month revenue series
  const monthly = (() => {
    const arr = [];
    const ref = new Date(now.getFullYear(), now.getMonth(), 1);
    for (let i = 5; i >= 0; i--) {
      const d = new Date(ref.getFullYear(), ref.getMonth() - i, 1);
      const next = new Date(ref.getFullYear(), ref.getMonth() - i + 1, 1);
      let cash = 0;
      let card = 0;
      bookings.forEach((b) => {
        try {
          const ts = new Date(b.created_at);
          if (ts >= d && ts < next) {
            if (b.payment_method === "cash") cash += b.price || 0;
            else if (b.payment_method === "card") card += b.price || 0;
          }
        } catch {}
      });
      arr.push({ label: format(d, "MMM"), cash, card, total: cash + card });
    }
    return arr;
  })();

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/bookings/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revive-bookings-${format(new Date(), "yyyyMMdd")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-overlay">
      <header className="border-b border-[#1A1A1A] bg-[#0A0A0A]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="h-4 w-4 text-[#DC2626]" />
            <div>
              <div className="font-display font-bold text-lg tracking-tight">REVIVE · ADMIN</div>
              <div className="font-mono-accent text-[10px] text-zinc-500">{adminEmail}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-back-link"
              className="hidden sm:block font-mono-accent text-xs text-zinc-400 hover:text-white"
            >
              ← VIEW SITE
            </a>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#DC2626] hover:text-[#DC2626] px-4 py-2 font-mono-accent text-xs"
            >
              <LogOut className="h-3.5 w-3.5" /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* DAILY TAKE — hero tile */}
        <div data-testid="daily-take" className="relative bg-gradient-to-br from-[#DC2626] via-[#991B1B] to-[#450a0a] border border-[#DC2626] p-8 md:p-10 mb-10 overflow-hidden">
          <div className="absolute right-[-40px] top-[-40px] font-display font-bold text-[240px] leading-none text-white/[0.04] tracking-tighter select-none pointer-events-none">
            ${(todayCash + todayCard).toFixed(0)}
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="h-4 w-4 text-white" />
              <span className="font-mono-accent text-[10px] text-white/80">
                DAILY TAKE · {format(new Date(), "EEE, MMM d, yyyy").toUpperCase()}
              </span>
            </div>
            <div className="font-display font-bold text-white text-6xl md:text-8xl leading-none tracking-tighter mb-6" data-testid="daily-take-amount">
              ${(todayCash + todayCard).toFixed(0)}
            </div>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-white">
                <Banknote className="h-4 w-4" />
                <span className="font-mono-accent text-[10px] text-white/70">CASH</span>
                <span className="font-display font-bold text-xl" data-testid="daily-cash">${todayCash.toFixed(0)}</span>
              </div>
              <div className="w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2 text-white">
                <CreditCard className="h-4 w-4" />
                <span className="font-mono-accent text-[10px] text-white/70">CARD</span>
                <span className="font-display font-bold text-xl" data-testid="daily-card">${todayCard.toFixed(0)}</span>
              </div>
              <div className="w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2 text-white">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-mono-accent text-[10px] text-white/70">JOBS DONE</span>
                <span className="font-display font-bold text-xl" data-testid="daily-jobs">{todayCompleted.length}</span>
              </div>
              {todayUnpaid > 0 && (
                <>
                  <div className="w-px h-6 bg-white/20" />
                  <div className="flex items-center gap-2 text-yellow-200">
                    <Clock className="h-4 w-4" />
                    <span className="font-mono-accent text-[10px] text-yellow-200/80">UNPAID TODAY</span>
                    <span className="font-display font-bold text-xl">{todayUnpaid}</span>
                  </div>
                </>
              )}
            </div>
            {todayCompleted.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="font-mono-accent text-[10px] text-white/60 mb-3">JOBS COMPLETED TODAY</div>
                <div className="space-y-2">
                  {todayCompleted.map((b) => (
                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 text-sm text-white/90">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-display font-bold truncate">{b.name}</span>
                        <span className="text-white/60 truncate">· {b.service}</span>
                      </div>
                      <div className="flex items-center gap-2 font-mono-accent text-[11px]">
                        {b.payment_method === "cash" && <Banknote className="h-3 w-3" />}
                        {b.payment_method === "card" && <CreditCard className="h-3 w-3" />}
                        <span>{(b.payment_method || "unpaid").toUpperCase()}</span>
                        <span className="text-white">${b.price || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>


        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="TOTAL BOOKINGS" value={bookings.length} />
          <Stat label="PENDING" value={pendingCount} accent />
          <Stat label="ENQUIRIES" value={contacts.length} />
          <Stat
            label="THIS WEEK"
            value={bookings.filter((b) => {
              try {
                return new Date(b.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
              } catch {
                return false;
              }
            }).length}
          />
        </div>

        {/* Revenue this month */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Stat label="CASH IN · THIS MONTH" value={`$${cashIn.toFixed(0)}`} mono="emerald" />
          <Stat label="CARD IN · THIS MONTH" value={`$${cardIn.toFixed(0)}`} mono="sky" />
          <Stat label="TOTAL IN · THIS MONTH" value={`$${totalIn.toFixed(0)}`} accent />
        </div>

        {/* Quote pipeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <Stat label="OPEN QUOTES" value={openQuotes} />
          <Stat label="QUOTED · THIS MONTH" value={`$${quotedTotal.toFixed(0)}`} mono="sky" />
          <Stat label="WON · THIS MONTH" value={`$${wonTotal.toFixed(0)}`} mono="emerald" />
        </div>

        {/* 6-month revenue chart */}
        <div className="bg-[#121212] border border-[#27272A] p-6 mb-10" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono-accent text-[10px] text-zinc-500">LAST 6 MONTHS · REVENUE</div>
            <div className="font-mono-accent text-[10px] text-zinc-600">AUD</div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="label" stroke="#71717A" tick={{ fontSize: 11 }} />
                <YAxis stroke="#71717A" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 0, color: "#fff" }}
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  formatter={(v) => `$${v}`}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
                <Bar dataKey="cash" stackId="a" name="Cash" fill="#10B981" />
                <Bar dataKey="card" stackId="a" name="Card" fill="#0EA5E9" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tabs + actions + search */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex">
            <button
              data-testid="tab-bookings"
              onClick={() => setTab("bookings")}
              className={`px-5 py-3 font-display font-bold uppercase text-sm tracking-wider border ${
                tab === "bookings" ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-[#27272A] text-zinc-400 hover:text-white"
              }`}
            >
              Bookings ({bookings.length})
            </button>
            <button
              data-testid="tab-contacts"
              onClick={() => setTab("contacts")}
              className={`px-5 py-3 font-display font-bold uppercase text-sm tracking-wider border-l-0 border ${
                tab === "contacts" ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-[#27272A] text-zinc-400 hover:text-white"
              }`}
            >
              Enquiries ({contacts.length})
            </button>
            <button
              data-testid="tab-gallery"
              onClick={() => setTab("gallery")}
              className={`px-5 py-3 font-display font-bold uppercase text-sm tracking-wider border-l-0 border ${
                tab === "gallery" ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-[#27272A] text-zinc-400 hover:text-white"
              }`}
            >
              Gallery ({gallery.length})
            </button>
            <button
              data-testid="tab-customers"
              onClick={() => setTab("customers")}
              className={`px-5 py-3 font-display font-bold uppercase text-sm tracking-wider border-l-0 border ${
                tab === "customers" ? "bg-[#DC2626] border-[#DC2626] text-white" : "border-[#27272A] text-zinc-400 hover:text-white"
              }`}
            >
              Customers ({customers.length})
            </button>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <input
                data-testid="admin-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full bg-[#121212] border border-[#27272A] focus:border-[#DC2626] text-white pl-10 pr-4 py-3 text-sm outline-none"
                placeholder="Search..."
              />
            </div>
            <button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 border border-[#27272A] hover:border-white text-zinc-200 hover:text-white font-mono-accent text-xs px-4 py-3 whitespace-nowrap"
            >
              <Download className="h-4 w-4" /> EXPORT CSV
            </button>
            <button
              data-testid="new-booking-btn"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#EF4444] text-white font-display font-bold uppercase tracking-wider text-xs px-4 py-3 whitespace-nowrap"
            >
              <Plus className="h-4 w-4" /> NEW BOOKING
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono-accent text-xs">LOADING...</div>
        ) : tab === "bookings" ? (
          <div className="space-y-4" data-testid="bookings-list">
            {fb.length === 0 && <Empty label="No bookings yet" />}
            {fb.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="bg-[#121212] border border-[#27272A] hover:border-[#3F3F46] transition-colors p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="font-display font-bold text-xl text-white tracking-tight">{b.name}</h3>
                      <StatusBadge status={b.status} />
                      <PaymentBadge method={b.payment_method} price={b.price} />
                      <SourceBadge source={b.source} />
                      <span className="font-mono-accent text-[10px] text-zinc-600">
                        {fmtDate(b.created_at)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <Info icon={Car} text={b.vehicle} />
                      <Info icon={Cal} text={`${b.service} — ${b.preferred_date} @ ${b.preferred_time}`} />
                      <a href={`tel:${b.phone}`} className="hover:text-white">
                        <Info icon={Phone} text={b.phone} />
                      </a>
                      <a href={`mailto:${b.email}`} className="hover:text-white">
                        <Info icon={Mail} text={b.email} />
                      </a>
                    </div>
                    {b.notes && (
                      <div className="mt-3 text-sm text-zinc-400 border-l-2 border-[#DC2626] pl-3 italic">
                        {b.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 lg:flex-col lg:items-end">
                    {b.payment_method !== "cash" && (
                      <ActionBtn icon={Banknote} label="CASH" onClick={() => promptPaid(b, "cash")} testid={`cash-${b.id}`} good />
                    )}
                    {b.payment_method !== "card" && (
                      <ActionBtn icon={CreditCard} label="CARD" onClick={() => promptPaid(b, "card")} testid={`card-${b.id}`} good />
                    )}
                    {b.status !== "confirmed" && (
                      <ActionBtn icon={CheckCircle2} label="CONFIRM" onClick={() => updateStatus(b.id, "confirmed")} testid={`confirm-${b.id}`} />
                    )}
                    {b.status !== "completed" && (
                      <ActionBtn icon={Clock} label="COMPLETE" onClick={() => updateStatus(b.id, "completed")} testid={`complete-${b.id}`} />
                    )}
                    {b.status !== "cancelled" && (
                      <ActionBtn icon={X} label="CANCEL" onClick={() => updateStatus(b.id, "cancelled")} testid={`cancel-${b.id}`} />
                    )}
                    <ActionBtn icon={Trash2} label="DELETE" onClick={() => removeBooking(b.id)} testid={`delete-booking-${b.id}`} danger />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "contacts" ? (
          <div className="space-y-4" data-testid="contacts-list">
            {fc.length === 0 && <Empty label="No enquiries yet" />}
            {fc.map((c) => (
              <ContactRow key={c.id} c={c} onPatch={patchContact} onDelete={removeContact} />
            ))}
          </div>
        ) : tab === "gallery" ? (
          <GalleryManager gallery={gallery} setGallery={setGallery} />
        ) : (
          <CustomerDirectory customers={customers} query={q} />
        )}
      </main>

      <NewBookingDialog open={creating} onClose={() => setCreating(false)} onCreated={addBooking} />
    </div>
  );
};

const ContactRow = ({ c, onPatch, onDelete }) => {
  const [price, setPrice] = useState(c.quoted_price || "");
  const [notes, setNotes] = useState(c.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const status = c.enquiry_status || "new";

  const statusMap = {
    new: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    quoted: "bg-sky-500/10 text-sky-400 border-sky-500/30",
    won: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    lost: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  };

  const saveDetails = async () => {
    setSaving(true);
    const patch = {
      quoted_price: Number(price) || 0,
      admin_notes: notes,
    };
    if (Number(price) > 0 && status === "new") patch.enquiry_status = "quoted";
    await onPatch(c.id, patch);
    setSaving(false);
    toast.success("Saved");
  };

  const setStatus = (s) => onPatch(c.id, { enquiry_status: s });

  return (
    <div
      data-testid={`contact-row-${c.id}`}
      className="bg-[#121212] border border-[#27272A] hover:border-[#3F3F46] transition-colors p-6"
    >
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h3 className="font-display font-bold text-xl text-white tracking-tight">{c.name}</h3>
            <span className={`inline-flex items-center text-[10px] font-mono-accent border px-2 py-1 ${statusMap[status]}`}>
              {status.toUpperCase()}
            </span>
            {c.quote_type && (
              <span className="inline-flex items-center text-[10px] font-mono-accent border border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626] px-2 py-1">
                QUOTE · {c.quote_type.toUpperCase()}
              </span>
            )}
            {c.quoted_price > 0 && (
              <span className="inline-flex items-center text-[10px] font-mono-accent border border-zinc-700 text-zinc-300 px-2 py-1">
                QUOTED ${c.quoted_price}
              </span>
            )}
            <span className="font-mono-accent text-[10px] text-zinc-600">{fmtDate(c.created_at)}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
            <a href={`mailto:${c.email}`} className="hover:text-white">
              <Info icon={Mail} text={c.email} />
            </a>
            {c.phone && (
              <a href={`tel:${c.phone}`} className="hover:text-white">
                <Info icon={Phone} text={c.phone} />
              </a>
            )}
            {c.vehicle && <Info icon={Car} text={c.vehicle} />}
            {c.finish_preference && <Info icon={ImageIcon} text={`Finish: ${c.finish_preference}`} />}
          </div>
          <div className="text-sm text-zinc-300 border-l-2 border-[#DC2626] pl-3 whitespace-pre-wrap">
            {c.message}
          </div>
          {Array.isArray(c.photos) && c.photos.length > 0 && (
            <div className="mt-4">
              <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">
                ATTACHED PHOTOS ({c.photos.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {c.photos.map((src, i) => (
                  <a
                    key={i}
                    href={src}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`quote-photo-${c.id}-${i}`}
                    className="w-24 h-24 border border-[#27272A] hover:border-[#DC2626] overflow-hidden block"
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <ActionBtn icon={Trash2} label="DELETE" onClick={() => onDelete(c.id)} testid={`delete-contact-${c.id}`} danger />
      </div>

      <div className="mt-4 pt-4 border-t border-[#27272A] grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-3">
          <label className="font-mono-accent text-[10px] text-zinc-500 mb-1 block">QUOTED PRICE (AUD)</label>
          <input
            data-testid={`contact-price-${c.id}`}
            type="number"
            min="0"
            step="1"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="e.g. 1800"
            className="w-full bg-transparent border border-[#27272A] focus:border-[#DC2626] text-white py-2 px-3 text-sm outline-none"
          />
        </div>
        <div className="md:col-span-6">
          <label className="font-mono-accent text-[10px] text-zinc-500 mb-1 block">INTERNAL NOTES</label>
          <textarea
            data-testid={`contact-notes-${c.id}`}
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Quote details, follow-up plan, vehicle specifics..."
            className="w-full bg-transparent border border-[#27272A] focus:border-[#DC2626] text-white py-2 px-3 text-sm outline-none resize-none"
          />
        </div>
        <div className="md:col-span-3 flex md:flex-col gap-2">
          <button
            data-testid={`contact-save-${c.id}`}
            onClick={saveDetails}
            disabled={saving}
            className="flex-1 font-mono-accent text-[10px] border border-[#DC2626] bg-[#DC2626]/10 hover:bg-[#DC2626] text-white py-2 px-3 transition-colors disabled:opacity-60"
          >
            {saving ? "SAVING..." : "SAVE"}
          </button>
          <div className="flex-1 grid grid-cols-3 gap-1">
            {["quoted", "won", "lost"].map((s) => (
              <button
                key={s}
                data-testid={`contact-status-${c.id}-${s}`}
                onClick={() => setStatus(s)}
                className={`font-mono-accent text-[9px] border py-2 transition-colors ${
                  status === s
                    ? statusMap[s]
                    : "border-[#27272A] text-zinc-500 hover:text-white hover:border-zinc-500"
                }`}
              >
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerDirectory = ({ customers, query }) => {
  const q = query.toLowerCase().trim();
  const filtered = customers.filter(
    (c) => !q || [c.name, c.email, c.phone, c.last_service, (c.vehicles || []).join(" ")].some((v) => (v || "").toLowerCase().includes(q))
  );

  const copy = (val, label = "Copied") => {
    if (!val) return;
    navigator.clipboard.writeText(val).then(
      () => toast.success(`${label}: ${val}`),
      () => toast.error("Copy failed")
    );
  };

  const copyAllEmails = () => {
    const list = customers.map((c) => c.email).filter(Boolean).join(", ");
    if (!list) {
      toast.error("No emails to copy");
      return;
    }
    navigator.clipboard.writeText(list).then(
      () => toast.success(`Copied ${customers.filter((c) => c.email).length} emails`),
      () => toast.error("Copy failed")
    );
  };

  const copyAllPhones = () => {
    const list = customers.map((c) => c.phone).filter(Boolean).join(", ");
    if (!list) {
      toast.error("No phone numbers to copy");
      return;
    }
    navigator.clipboard.writeText(list).then(
      () => toast.success(`Copied ${customers.filter((c) => c.phone).length} phone numbers`),
      () => toast.error("Copy failed")
    );
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/customers/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revive-customers-${format(new Date(), "yyyyMMdd")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const withBookings = customers.filter((c) => c.bookings_count > 0).length;
  const enquiriesOnly = customers.filter((c) => c.bookings_count === 0 && c.enquiries_count > 0).length;

  return (
    <div data-testid="customer-directory">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="TOTAL CUSTOMERS" value={customers.length} />
        <Stat label="WITH BOOKINGS" value={withBookings} mono="emerald" />
        <Stat label="ENQUIRIES ONLY" value={enquiriesOnly} mono="sky" />
        <Stat label="SHOWING" value={filtered.length} />
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button
          data-testid="copy-all-emails"
          onClick={copyAllEmails}
          className="inline-flex items-center gap-2 border border-[#27272A] hover:border-white text-zinc-200 hover:text-white font-mono-accent text-xs px-4 py-2.5"
        >
          <Copy className="h-3.5 w-3.5" /> COPY ALL EMAILS
        </button>
        <button
          data-testid="copy-all-phones"
          onClick={copyAllPhones}
          className="inline-flex items-center gap-2 border border-[#27272A] hover:border-white text-zinc-200 hover:text-white font-mono-accent text-xs px-4 py-2.5"
        >
          <Copy className="h-3.5 w-3.5" /> COPY ALL PHONES
        </button>
        <button
          data-testid="customer-export-csv"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 border border-[#27272A] hover:border-white text-zinc-200 hover:text-white font-mono-accent text-xs px-4 py-2.5"
        >
          <Download className="h-3.5 w-3.5" /> EXPORT CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <Empty label="No customers yet" />
      ) : (
        <div className="border border-[#27272A]">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#0A0A0A] border-b border-[#27272A] font-mono-accent text-[10px] text-zinc-500">
            <div className="col-span-3">CUSTOMER</div>
            <div className="col-span-3">EMAIL</div>
            <div className="col-span-2">PHONE</div>
            <div className="col-span-2">ACTIVITY</div>
            <div className="col-span-2 text-right">SPENT / LAST</div>
          </div>
          {filtered.map((c) => (
            <div
              key={c.key}
              data-testid={`customer-row-${c.key}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-4 border-b border-[#1A1A1A] last:border-b-0 hover:bg-[#141414] transition-colors items-center"
            >
              <div className="md:col-span-3 min-w-0">
                <div className="font-display font-bold text-white truncate">{c.name || "—"}</div>
                {c.vehicles && c.vehicles.length > 0 && (
                  <div className="text-xs text-zinc-500 truncate mt-0.5 flex items-center gap-1.5">
                    <Car className="h-3 w-3" /> {c.vehicles.join(", ")}
                  </div>
                )}
              </div>
              <div className="md:col-span-3 min-w-0">
                {c.email ? (
                  <button
                    onClick={() => copy(c.email, "Email")}
                    data-testid={`copy-email-${c.key}`}
                    className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm truncate max-w-full"
                  >
                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{c.email}</span>
                  </button>
                ) : (
                  <span className="text-zinc-600 text-sm">—</span>
                )}
              </div>
              <div className="md:col-span-2 min-w-0">
                {c.phone ? (
                  <button
                    onClick={() => copy(c.phone, "Phone")}
                    data-testid={`copy-phone-${c.key}`}
                    className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm truncate"
                  >
                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{c.phone}</span>
                  </button>
                ) : (
                  <span className="text-zinc-600 text-sm">—</span>
                )}
              </div>
              <div className="md:col-span-2 text-xs text-zinc-400 font-mono-accent">
                {c.bookings_count > 0 && (
                  <span className="text-emerald-400 mr-2">{c.bookings_count} BOOKING{c.bookings_count > 1 ? "S" : ""}</span>
                )}
                {c.enquiries_count > 0 && (
                  <span className="text-sky-400">{c.enquiries_count} ENQUIR{c.enquiries_count > 1 ? "IES" : "Y"}</span>
                )}
              </div>
              <div className="md:col-span-2 md:text-right text-xs">
                {c.total_spent > 0 ? (
                  <div className="font-display font-bold text-white">${Math.round(c.total_spent)}</div>
                ) : (
                  <div className="text-zinc-600">—</div>
                )}
                <div className="text-zinc-500 mt-0.5">{c.last_seen ? format(new Date(c.last_seen), "MMM d, yyyy") : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GalleryManager = ({ gallery, setGallery }) => {
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    let uploaded = 0;
    let failed = 0;
    try {
      for (const file of files) {
        try {
          if (!file.type || !file.type.startsWith("image/")) {
            toast.error(`${file.name}: not an image, skipped`);
            failed += 1;
            continue;
          }
          const dataUrl = await compressImage(file, { maxDim: 2400, quality: 0.85 });
          if (!dataUrl) {
            toast.error(`${file.name}: could not read image`);
            failed += 1;
            continue;
          }
          const { data } = await axios.post(
            `${API}/admin/gallery`,
            { image_base64: dataUrl, caption: "" },
            { headers: authHeader() }
          );
          setGallery((g) => [data, ...g]);
          uploaded += 1;
        } catch (fileErr) {
          console.error("Upload failed for", file.name, fileErr);
          const detail = fileErr?.response?.data?.detail;
          toast.error(`${file.name}: ${typeof detail === "string" ? detail : fileErr.message || "upload failed"}`);
          failed += 1;
        }
      }
      if (uploaded > 0) toast.success(`Uploaded ${uploaded} photo${uploaded === 1 ? "" : "s"}${failed ? ` (${failed} failed)` : ""}`);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeader() });
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="gallery-manager">
      <label
        data-testid="gallery-upload"
        className={`relative flex flex-col items-center justify-center border-2 border-dashed border-[#27272A] hover:border-[#DC2626] bg-[#121212] py-16 mb-6 cursor-pointer transition-colors ${
          busy ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        <Upload className="h-8 w-8 text-[#DC2626] mb-3" />
        <div className="font-display font-bold uppercase text-white tracking-tighter text-2xl mb-1">
          {busy ? "Uploading..." : "Drop photos to upload"}
        </div>
        <div className="font-mono-accent text-[10px] text-zinc-500">
          OR CLICK · MULTIPLE FILES OK · AUTO-COMPRESSED · JPG / PNG / WEBP
        </div>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={onPick}
          className="absolute inset-0 opacity-0 cursor-pointer"
          data-testid="gallery-file-input"
        />
      </label>

      {gallery.length === 0 ? (
        <Empty label="No photos uploaded yet — gallery will fall back to defaults on the website" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {gallery.map((it) => (
            <div
              key={it.id}
              data-testid={`gallery-tile-${it.id}`}
              className="relative aspect-square border border-[#27272A] overflow-hidden group"
            >
              <img src={it.image_base64} alt="" className="w-full h-full object-cover" />
              <button
                data-testid={`gallery-delete-${it.id}`}
                onClick={() => remove(it.id)}
                className="absolute top-2 right-2 bg-black/70 hover:bg-[#DC2626] text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NewBookingDialog = ({ open, onClose, onCreated }) => {
  const empty = {
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    service: "",
    preferred_date: format(new Date(), "yyyy-MM-dd"),
    preferred_time: "09:00",
    notes: "",
    price: "",
    payment_method: "unpaid",
    status: "confirmed",
  };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setF(empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.vehicle || !f.service) {
      toast.error("Name, phone, vehicle and service are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...f, price: Number(f.price) || 0 };
      const { data } = await axios.post(`${API}/admin/bookings`, payload, { headers: authHeader() });
      onCreated(data);
      toast.success("Walk-in booking added");
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create booking");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#27272A] focus:border-[#DC2626] text-white py-2.5 px-3 placeholder-zinc-600 focus:outline-none text-sm";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="new-booking-dialog"
        className="max-w-2xl bg-[#0F0F0F] border-[#27272A] text-white rounded-none p-0 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="p-6 border-b border-[#27272A]">
          <DialogTitle className="font-display font-bold uppercase text-2xl tracking-tighter">
            New Walk-in Booking
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DField label="Name *">
              <input data-testid="nb-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} />
            </DField>
            <DField label="Phone *">
              <input data-testid="nb-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            </DField>
            <DField label="Email (optional — sends confirmation)">
              <input data-testid="nb-email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} />
            </DField>
            <DField label="Vehicle *">
              <input data-testid="nb-vehicle" className={inputCls} value={f.vehicle} onChange={(e) => set("vehicle", e.target.value)} placeholder="e.g. BMW M3 2022" />
            </DField>
            <DField label="Service *">
              <input data-testid="nb-service" className={inputCls} value={f.service} onChange={(e) => set("service", e.target.value)} placeholder="e.g. Interior Deep Clean" />
            </DField>
            <DField label="Date">
              <input data-testid="nb-date" type="date" className={inputCls} value={f.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
            </DField>
            <DField label="Time">
              <input data-testid="nb-time" type="time" className={inputCls} value={f.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} />
            </DField>
            <DField label="Price (AUD)">
              <input data-testid="nb-price" type="number" min="0" step="1" className={inputCls} value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. 140" />
            </DField>
            <DField label="Payment">
              <div className="flex gap-2">
                {["unpaid", "cash", "card"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`nb-pay-${m}`}
                    onClick={() => set("payment_method", m)}
                    className={`flex-1 py-2.5 font-mono-accent text-[10px] border ${
                      f.payment_method === m ? "border-[#DC2626] bg-[#DC2626]/10 text-white" : "border-[#27272A] text-zinc-400"
                    }`}
                  >
                    {m.toUpperCase()}
                  </button>
                ))}
              </div>
            </DField>
            <DField label="Status">
              <div className="flex gap-2">
                {["confirmed", "completed", "pending"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`nb-status-${s}`}
                    onClick={() => set("status", s)}
                    className={`flex-1 py-2.5 font-mono-accent text-[10px] border ${
                      f.status === s ? "border-[#DC2626] bg-[#DC2626]/10 text-white" : "border-[#27272A] text-zinc-400"
                    }`}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>
            </DField>
          </div>
          <DField label="Notes">
            <textarea data-testid="nb-notes" rows={2} className={`${inputCls} resize-none`} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </DField>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-3 font-mono-accent text-xs border border-[#27272A] text-zinc-300 hover:border-white">
              CANCEL
            </button>
            <button
              type="submit"
              data-testid="nb-submit"
              disabled={busy}
              className="px-6 py-3 font-display font-bold uppercase tracking-wider text-sm bg-[#DC2626] hover:bg-[#EF4444] text-white disabled:opacity-60"
            >
              {busy ? "Saving..." : "Add Booking"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DField = ({ label, children }) => (
  <div>
    <label className="font-mono-accent text-[10px] text-zinc-500 mb-1.5 block">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className={`p-6 border ${accent ? "border-[#DC2626] bg-[#DC2626]/5" : "border-[#27272A] bg-[#121212]"}`}>
    <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">{label}</div>
    <div className="font-display font-bold text-4xl text-white">{value}</div>
  </div>
);

const Info = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 text-zinc-300">
    <Icon className="h-4 w-4 text-zinc-500 flex-shrink-0" />
    <span className="truncate">{text}</span>
  </div>
);

const ActionBtn = ({ icon: Icon, label, onClick, danger, good, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-3 py-2 font-mono-accent text-[10px] border transition-colors ${
      danger
        ? "border-[#27272A] text-zinc-500 hover:border-red-500 hover:text-red-500"
        : good
        ? "border-[#27272A] text-emerald-400 hover:border-emerald-500 hover:bg-emerald-500/10"
        : "border-[#27272A] text-zinc-300 hover:border-white hover:text-white"
    }`}
  >
    <Icon className="h-3 w-3" /> {label}
  </button>
);

const Empty = ({ label }) => (
  <div className="text-center py-20 border border-dashed border-[#27272A] text-zinc-500 font-mono-accent text-xs">
    {label}
  </div>
);

export default function Admin() {
  const [adminEmail, setAdminEmail] = useState(null);
  const [checked, setChecked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecked(true);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setAdminEmail(data.email))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecked(true));
  }, []);

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAdminEmail(null);
    navigate("/admin");
  };

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-500 font-mono-accent text-xs">
        CHECKING SESSION...
      </div>
    );
  }
  if (!adminEmail) return <Login onAuth={setAdminEmail} />;
  return <Dashboard adminEmail={adminEmail} onLogout={onLogout} />;
}
